/**
 * Friendly logging with emojis for key operations
 * Only logs in development mode (except errors which always log)
 */
const log = {
  info: (emoji, message, data) => {
    if (process.env.NODE_ENV === 'development') {
      console.log(`${emoji} ${message}`, data !== undefined ? data : '');
    }
  },
  success: (emoji, message, data) => {
    if (process.env.NODE_ENV === 'development') {
      console.log(`${emoji} ${message}`, data !== undefined ? data : '');
    }
  },
  error: (emoji, message, error) => {
    console.error(`${emoji} ${message}`, error?.message || error || '');
  },
  warn: (emoji, message, data) => {
    console.warn(`${emoji} ${message}`, data !== undefined ? data : '');
  }
};

class CloudAPIClient {
  constructor() {
    this.baseURL = this.loadApiUrl();
    this.authToken = null;
    this.workspaceId = null;
    this.workspaces = [];
    this.viewAsUserId = null;
    this.onAuthError = null;
    this.tokenKey = "cloudAuthToken";
    this.workspaceKey = "cloudWorkspaceId";
    this.apiUrlKey = "cloudApiUrl";
    this.extensionSessionCookieName = "uws_ext_session_token";
    this.extensionSessionCookieMaxAgeSeconds = 300;
    this.initAuth();
  }

  /**
   * Load API URL from storage or use default
   */
  loadApiUrl() {
    try {
      // Try to load from localStorage first
      if (typeof window !== 'undefined' && window.localStorage) {
        const storedUrl = localStorage.getItem(this.apiUrlKey || "cloudApiUrl");
        if (storedUrl) {
          return storedUrl;
        }
      }
    } catch (error) {
      log.warn('⚠️', 'Failed to load API URL from storage:', error);
    }
    // Fallback to environment variable or default
    return process.env.CLOUD_BACKEND_API || "http://localhost:3000";
  }

  async initAuth() {
    this.authToken = localStorage.getItem(this.tokenKey);
    this.workspaceId = localStorage.getItem(this.workspaceKey);
  }

  async setAuthToken(token) {
    this.authToken = token;
    localStorage.setItem(this.tokenKey, token);
  }

  async clearAuthToken() {
    this.authToken = null;
    localStorage.removeItem(this.tokenKey);
  }

  async setWorkspaceId(workspaceId) {
    this.workspaceId = workspaceId;
    localStorage.setItem(this.workspaceKey, workspaceId);
  }

  async clearWorkspaceId() {
    this.workspaceId = null;
    localStorage.removeItem(this.workspaceKey);
  }

  async handleAuthFailure() {
    await this.clearAuthToken();
    this.onAuthError?.();
  }

  /**
   * Call an admin endpoint. Admin routes are mounted at /admin/api (not /api), so request() —
   * which prepends /api — can't be used for them.
   * @param {string} path - path below /admin/api, e.g. '/shopify/detect'
   * @param {object} [options] - fetch options; a plain `body` object is JSON-encoded for you
   */
  async adminRequest(path, options = {}) {
    const { body, ...rest } = options;
    const headers = { 'Content-Type': 'application/json', ...options.headers };
    if (this.authToken) headers['Authorization'] = `Bearer ${this.authToken}`;
    if (this.workspaceId) headers['X-Workspace-Id'] = this.workspaceId;

    const response = await fetch(`${this.baseURL}/admin/api${path}`, {
      ...rest,
      headers,
      ...(body !== undefined && { body: typeof body === 'string' ? body : JSON.stringify(body) }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      // validate() middleware returns a generic `error` plus per-field `errors` — surface the
      // specifics, otherwise every bad field just reads "Validation Error".
      const fieldErrors = Array.isArray(errorData.errors)
        ? errorData.errors.map((e) => `${e.field}: ${e.message}`).join('; ')
        : '';
      const base = errorData.error || errorData.message || `Request failed (${response.status})`;
      const error = new Error(fieldErrors ? `${base} — ${fieldErrors}` : base);
      error.code = errorData.code;
      error.status = response.status;
      throw error;
    }
    return response.json();
  }

  async searchAdminUsers(search = '', limit = 10) {
    return this.adminRequest(`/users?search=${encodeURIComponent(search)}&limit=${limit}`);
  }

  getCurrentWorkspaceId() {
    return this.workspaceId;
  }

  getWorkspaces() {
    return this.workspaces;
  }

  /**
   * Get current API URL
   */
  getApiUrl() {
    return this.baseURL;
  }

  /**
   * Set new API URL and persist to storage
   */
  async setApiUrl(url) {
    try {
      this.baseURL = url;

      if (typeof window !== 'undefined' && window.localStorage) {
        localStorage.setItem(this.apiUrlKey, url);
      }

      return true;
    } catch (error) {
      log.error('💥', 'Failed to set API URL:', error);
      throw error;
    }
  }

  /**
   * Reset API URL to default (environment variable or localhost)
   */
  async resetApiUrl() {
    const defaultUrl = process.env.CLOUD_BACKEND_API || "http://localhost:3000";

    try {
      this.baseURL = defaultUrl;

      if (typeof window !== 'undefined' && window.localStorage) {
        localStorage.removeItem(this.apiUrlKey);
      }

      return defaultUrl;
    } catch (error) {
      log.error('💥', 'Failed to reset API URL:', error);
      throw error;
    }
  }

  /**
   * Convert UTC date string to local Date object
   * Backend returns UTC timestamps, this ensures they're properly converted to local time
   */
  toLocalDate(utcDateString) {
    if (!utcDateString) return null;

    // Ensure the string is treated as UTC by appending 'Z' if not present
    let dateStr = utcDateString;
    if (!dateStr.endsWith('Z') && !dateStr.includes('+') && !dateStr.includes('T')) {
      // If it's just a date without timezone, assume it's UTC
      dateStr = dateStr + 'Z';
    } else if (dateStr.includes('T') && !dateStr.endsWith('Z') && !dateStr.includes('+') && !dateStr.includes('-', 10)) {
      // If it has time but no timezone indicator, append Z
      dateStr = dateStr + 'Z';
    }

    const date = new Date(dateStr);
    return isNaN(date.getTime()) ? null : date;
  }

  async request(endpoint, options = {}) {
    const url = `${this.baseURL}/api${endpoint}`;
    const headers = {
      'Content-Type': 'application/json',
      ...options.headers,
    };

    if (this.authToken) {
      headers['Authorization'] = `Bearer ${this.authToken}`;
    }

    // Add workspace header if workspace is selected
    if (this.workspaceId) {
      headers['X-Workspace-Id'] = this.workspaceId;
    }

    // Add view-as header when admin is viewing another user's context
    // Per-request header (from options.headers) takes precedence over global
    if (!headers['X-View-As-User-Id'] && this.viewAsUserId) {
      headers['X-View-As-User-Id'] = this.viewAsUserId;
    }

    const startTime = Date.now();

    try {
      const response = await fetch(url, {
        ...options,
        headers,
      });

      const duration = Date.now() - startTime;
      if (duration > 2000) {
        log.warn('🐢', `Slow request: ${endpoint} took ${duration}ms`);
      }

      if (response.status === 401) {
        // Token expired or invalid - clear auth token and notify listeners
        await this.handleAuthFailure();
        throw new Error("Authentication required. Please log in again.");
      }

      if (response.status === 403) {
        // Forbidden - don't clear token (could be permissions issue, not auth).
        // Surface the backend's specific reason (e.g. "Only workspace owners
        // can perform this action") instead of a generic auth-redirect message.
        const errorData = await response.json().catch(() => ({}));
        const message =
          errorData.message ||
          errorData.error ||
          'You do not have permission to perform this action.';
        const error = new Error(message);
        error.statusCode = 403;
        if (errorData.code) error.code = errorData.code;
        throw error;
      }

      if (response.status === 503) {
        // Service unavailable - temporary infrastructure issue, don't clear token
        const error = new Error("Service temporarily unavailable. Please try again in a moment.");
        error.isRetriable = true;
        error.statusCode = 503;
        throw error;
      }

      if (response.status === 429) {
        // Not always a rate limit — some endpoints use 429 for quota errors
        // (e.g. MCP token limit reached), so prefer the backend's reason.
        const errorData = await response.json().catch(() => ({}));
        const message =
          (errorData.errors?.[0]?.message) ||
          errorData.message ||
          errorData.error ||
          'Too many requests. Please wait a moment and try again.';
        const error = new Error(message);
        error.statusCode = 429;
        if (errorData.code) {
          error.code = errorData.code;
        }
        if (errorData.current !== undefined) {
          error.current = errorData.current;
        }
        if (errorData.limit !== undefined) {
          error.limit = errorData.limit;
        }
        throw error;
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const message =
          (errorData.errors?.[0]?.message) ||
          errorData.message ||
          errorData.error ||
          `Request failed (${response.status})`;
        const error = new Error(message);
        error.statusCode = response.status;
        if (errorData.code) {
          error.code = errorData.code;
        }
        throw error;
      }

      return await response.json();
    } catch (error) {
      // If error has no response status, it's a network error (API down, timeout, etc.)
      // Do NOT clear auth token for network errors - only for confirmed 401 responses
      if (error.message === 'Authentication required. Please log in again.') {
        // This is our 401 error from above, propagate it
        throw error;
      }

      // Check if this is a network error (connection refused, timeout, etc.)
      const isNetworkError = error.name === 'TypeError' ||
        error.message?.includes('Failed to fetch') ||
        error.message?.includes('Network request failed');

      if (isNetworkError && !options._isRetry) {
        // Retry once for network errors before giving up
        log.info('🔄', `Retrying ${endpoint} after network error...`);
        return this.request(endpoint, { ...options, _isRetry: true });
      }

      // Network error or other fetch failure - DO NOT clear token
      const duration = Date.now() - startTime;
      log.error('🔌', `Network error on ${endpoint} after ${duration}ms:`, error);

      // Create structured error for network failures so UI can show ConnectionError
      if (isNetworkError) {
        const networkError = new Error('Cannot connect to server. Please check your connection.');
        networkError.isNetworkError = true;
        networkError.isRetriable = true;
        networkError.originalError = error;
        throw networkError;
      }

      throw error;
    }
  }

  /**
   * Create a new task
   */
  async createTask(taskData) {
    log.info('📦', 'Creating new task...');

    try {
      const response = await this.request('/tasks', {
        method: 'POST',
        body: JSON.stringify(taskData)
      });
      log.success('✅', 'Task created');
      return response;
    } catch (error) {
      log.error('💥', 'Failed to create task:', error);
      throw error;
    }
  }

  /**
   * Get dashboard data (tasks with stats and recent runs)
   * @param {Object} options - { limit, offset, scheduledOnly }
   * @returns {Promise<{ automations: [], pagination: { total, limit, offset } }>}
   */
  async getAutomations(options = {}) {
    const { limit = 10, offset = 0, scheduledOnly = false, filters = {} } = options;
    log.info('📊', 'Fetching automations...', { limit, offset, scheduledOnly, filters });

    try {
      const params = new URLSearchParams();
      params.append('limit', limit);
      params.append('offset', offset);
      params.append('include_runs', 100);
      if (scheduledOnly) {
        params.append('scheduled_only', 'true');
      }
      if (filters.status?.length) {
        params.append('status', filters.status.join(','));
      }
      if (filters.taskType) {
        params.append('task_type', filters.taskType);
      }
      if (filters.sortBy && filters.sortBy !== 'created_at') {
        params.append('sort_by', filters.sortBy);
      }
      if (filters.sortOrder && filters.sortOrder !== 'desc') {
        params.append('sort_order', filters.sortOrder);
      }

      const response = await this.request(`/dashboard?${params.toString()}`);

      const result = {
        automations: response.tasks.map(task => this.transformTaskToAutomation(task)),
        pagination: response.pagination || { total: response.tasks.length, limit, offset },
        summary: response.summary || null,
        upcoming_scheduled: response.upcoming_scheduled || []
      };

      log.success('✅', `Loaded ${result.automations.length} automations`);
      return result;
    } catch (error) {
      log.error('💥', 'Failed to fetch automations:', error);
      throw error;
    }
  }

  /**
   * Transform backend task format to frontend automation format
   */
  transformTaskToAutomation(task) {
    const recentRuns = task.recent_runs || [];
    const lastRun = recentRuns[0];

    // Map backend status to frontend status
    const statusMap = {
      'completed': 'completed',
      'failed': 'completed', // Still completed, but with error
      'running': 'running',
      'pending': 'queued',
      'cancelled': 'completed',
      'stopped': 'completed'
    };

    // Determine overall task status based on most recent run
    let taskStatus = 'completed';
    if (lastRun) {
      taskStatus = statusMap[lastRun.status] || 'completed';
    }

    // Create timeline from recent runs (convert UTC to local time)
    const timeline = recentRuns.map(run => ({
      timestamp: this.toLocalDate(run.created_at)?.getTime() || Date.now(),
      status: this.mapRunStatusToTimelineStatus(run.status),
      runId: run.id,
      message: this.generateRunMessage(run),
      screenshotUrls: run.screenshot_urls || [],
      itemCount: run.item_count,
      extraction: run.extraction || null,
      last_journal_step: run.last_journal_step || null,
      catalogue: run.catalogue || null,
      stats: run.stats || null,
      subPageScan: run.sub_page_scan || null,
      hasCheckpoint: run.has_checkpoint || false,
      checkpointArchived: run.checkpoint_archived || false,
      stoppedReason: run.stopped_reason || null,
    }));

    // Parse cron expression to interval (simplified)
    const intervalMinutes = this.cronToInterval(task.cron_expression);

    return {
      id: task.id,
      updated_at: task.updated_at,
      name: task.name,
      type: task.task_type,
      website: task.metadata?.url,
      favicon: task.metadata?.favicon || this.generateFaviconUrl(task.metadata?.url),
      lastRun: this.toLocalDate(lastRun?.created_at),
      lastRunMessage: lastRun ? this.generateRunMessage(lastRun) : "No runs yet",
      status: taskStatus,
      screenshots: lastRun?.screenshot_urls || [],
      timeline,
      schedule: {
        enabled: task.schedule_enabled,
        interval: intervalMinutes,
        cronExpression: task.cron_expression || null,
        lastRun: this.toLocalDate(task.stats.last_run_at),
        nextRun: this.toLocalDate(task.next_scheduled_run),
      },
      progress: task.stats.success_rate * 100,
      change: 0, // Calculate from historical data if needed
      stats: task.stats,
      latestTableId: lastRun?.table_id || null,
    };
  }

  mapRunStatusToTimelineStatus(status) {
    const statusMap = {
      'completed': 'success',
      'failed': 'failed',
      'running': 'running',
      'pending': 'queued',
      'cancelled': 'cancelled',
      'stopped': 'stopped'
    };
    return statusMap[status] || 'success';
  }

  generateRunMessage(run) {
    const stats = run.run_metadata?.stats || run.stats;
    const extraction = run.run_metadata?.extraction || run.extraction;
    const rowCount = extraction?.rowCount || run.item_count || 0;
    const catalogue = run.catalogue || run.run_metadata?.catalogue;
    const lastJournal = run.last_journal_step
      || (Array.isArray(run.run_metadata?.journal) ? run.run_metadata.journal[run.run_metadata.journal.length - 1] : null);

    if (run.status === 'running') {
      if (catalogue?.summary) return catalogue.summary;
      if (catalogue?.card_status) return catalogue.card_status;
      if (lastJournal?.metadata?.Status) return lastJournal.metadata.Status;
      if (lastJournal?.description) return lastJournal.description;
      if (stats?.total) {
        return `Extracting \u2014 ${stats.processed || 0} of ${stats.total} URLs processed`;
      }
      return 'Extraction in progress...';
    }

    if (run.status === 'completed') {
      if (stats?.total) {
        return `Extracted ${rowCount} rows from ${stats.total} URLs`;
      }
      return rowCount > 0 ? `Extracted ${rowCount} rows` : 'Task completed successfully';
    }

    if (run.status === 'failed') {
      return run.error || 'Task encountered an error';
    }

    if (run.status === 'stopped') {
      const stoppedReason = run.run_metadata?.stoppedReason || run.stopped_reason;
      if (stoppedReason === 'insufficient_credits') {
        if (stats?.total) {
          return `Out of credits \u2014 ${stats.processed || 0} of ${stats.total} URLs processed`;
        }
        return rowCount > 0
          ? `Out of credits \u2014 ${rowCount} rows extracted before stopping`
          : 'Stopped \u2014 ran out of credits';
      }
      if (stats?.total) {
        return `Stopped \u2014 ${stats.processed || 0} of ${stats.total} URLs processed`;
      }
      return 'Task was stopped with partial results';
    }

    if (run.status === 'cancelled') {
      return 'Task was cancelled before starting';
    }

    if (run.status === 'pending') {
      return 'Task queued for execution';
    }

    return 'Task run completed';
  }

  static SCHEDULE_MAP = [
    { interval: 10,    cron: '*/10 * * * *' },  // every 10 minutes
    { interval: 30,    cron: '*/30 * * * *' },  // every 30 minutes
    { interval: 60,    cron: '0 * * * *' },     // every 1 hour
    { interval: 360,   cron: '0 */6 * * *' },   // every 6 hours
    { interval: 720,   cron: '0 */12 * * *' },  // every 12 hours
    { interval: 1440,  cron: '0 0 * * *' },     // every 1 day
    { interval: 2880,  cron: '0 0 */2 * *' },   // every 2 days
    { interval: 4320,  cron: '0 0 */3 * *' },   // every 3 days
    { interval: 10080, cron: '0 0 * * 0' },     // every week
    { interval: 43200, cron: '0 0 1 * *' },     // every month
  ];

  cronToInterval(cronExpression) {
    if (!cronExpression) return 0;
    const entry = CloudAPIClient.SCHEDULE_MAP.find(e => e.cron === cronExpression);
    return entry ? entry.interval : 0;
  }

  extractDomain(url) {
    if (!url) return null;
    try {
      const urlObj = new URL(url);
      return urlObj.hostname.replace('www.', '');
    } catch {
      return null;
    }
  }

  generateFaviconUrl(url) {
    if (!url) return null;
    try {
      const urlObj = new URL(url);
      return `${urlObj.protocol}//${urlObj.hostname}/favicon.ico`;
    } catch {
      return null;
    }
  }

  // =============================================================================
  // Billing & Stripe
  // =============================================================================

  async getBillingStatus() {
    return await this.request('/billing/status');
  }

  async getAvailablePlans(catalog) {
    return await this.request('/billing/plans' + (catalog ? `?catalog=${encodeURIComponent(catalog)}` : ''));
  }

  async createCheckoutSession(planName, interval = 'monthly', { source } = {}) {
    return await this.request('/billing/checkout', {
      method: 'POST',
      body: JSON.stringify({ planName, interval, source }),
    });
  }

  async createCreditCheckoutSession(priceId) {
    return await this.request('/billing/checkout/credits', {
      method: 'POST',
      body: JSON.stringify({ priceId }),
    });
  }

  async createPortalSession() {
    return await this.request('/billing/portal', {
      method: 'POST',
    });
  }

  // =============================================================================
  // Integrations
  // =============================================================================

  async getIntegrations() {
    const response = await this.request('/integrations');
    return response.integrations || [];
  }

  async getGoogleSheetsAuthUrl() {
    const response = await this.request('/integrations/google-sheets/auth-url');
    return response.authUrl;
  }

  async disconnectGoogleSheets() {
    await this.request('/integrations/google-sheets', { method: 'DELETE' });
  }

  async updateGoogleSheetsSettings(settings) {
    await this.request('/integrations/google-sheets/settings', {
      method: 'PATCH',
      body: JSON.stringify(settings),
    });
  }

  async getGooglePickerToken() {
    return await this.request('/integrations/google-sheets/picker-token');
  }

  async exportToGoogleSheets(tableId) {
    return await this.request(`/tables/${tableId}/export-to-sheets`, { method: 'POST' });
  }

  async testGoogleSheets() {
    return await this.request('/integrations/google-sheets/test', { method: 'POST' });
  }

  async saveWebhook(url) {
    return await this.request('/integrations/webhook', {
      method: 'POST',
      body: JSON.stringify({ url }),
    });
  }

  async deleteWebhook() {
    await this.request('/integrations/webhook', { method: 'DELETE' });
  }

  async updateWebhookSettings(settings) {
    return await this.request('/integrations/webhook/settings', {
      method: 'PATCH',
      body: JSON.stringify(settings),
    });
  }

  async testWebhook() {
    return await this.request('/integrations/webhook/test', { method: 'POST' });
  }

  async getWebhookSecret() {
    return await this.request('/integrations/webhook/secret');
  }

  async getIntegrationDeliveries(provider, { limit, offset } = {}) {
    const params = new URLSearchParams();
    if (provider) params.append('provider', provider);
    if (limit) params.append('limit', limit);
    if (offset) params.append('offset', offset);
    const qs = params.toString();
    return await this.request(`/integrations/deliveries${qs ? `?${qs}` : ''}`);
  }

  // =============================================================================
  // MCP
  // =============================================================================

  async getMcpTokens() {
    const response = await this.request('/mcp-tokens');
    return response.tokens || [];
  }

  async createMcpToken({ name, accessLevel }) {
    return await this.request('/mcp-tokens', {
      method: 'POST',
      body: JSON.stringify({ name, accessLevel }),
    });
  }

  async revokeMcpToken(tokenId) {
    return await this.request(`/mcp-tokens/${tokenId}`, { method: 'DELETE' });
  }

  // The MCP server is mounted at the API root, not under /api. This URL is what
  // users paste into their AI apps, so production advertises the public MCP host
  // (MCP_PUBLIC_URL — mcp.ultimatewebscraper.com) instead of the API host; both
  // serve the same endpoint and cloud-api URLs already pasted keep working. When
  // the API base is overridden via localStorage (dev/staging), snippets follow
  // the override so a local backend gets local URLs.
  getMcpServerUrl() {
    try {
      if (typeof window !== "undefined" && window.localStorage && localStorage.getItem(this.apiUrlKey)) {
        return `${this.baseURL}/mcp`;
      }
    } catch {
      // Storage unavailable — fall through to the advertised URL.
    }
    return process.env.MCP_PUBLIC_URL || `${this.baseURL}/mcp`;
  }

  // ---- OAuth (MCP connector consent + Connected apps) ----

  // params = the raw query params the /oauth/authorize redirect carried over.
  async validateOAuthRequest(params) {
    return await this.request('/oauth/authorize/validate', {
      method: 'POST',
      body: JSON.stringify({ params }),
    });
  }

  async decideOAuthRequest({ params, approved, workspaceId, accessLevel }) {
    return await this.request('/oauth/authorize/decision', {
      method: 'POST',
      body: JSON.stringify({ params, approved, workspaceId, accessLevel }),
    });
  }

  async getOAuthGrants() {
    const response = await this.request('/oauth/grants');
    return response.grants || [];
  }

  async revokeOAuthGrant(grantId) {
    return await this.request(`/oauth/grants/${grantId}`, { method: 'DELETE' });
  }

  /**
   * Run automation manually
   */
  async runAutomation(automationId) {
    log.info('🚀', `Starting automation ${automationId.slice(0, 8)}...`);

    try {
      const response = await this.request(`/tasks/${automationId}/run`, {
        method: 'POST'
      });

      log.success('✅', 'Automation started', { taskRunId: response.taskRunId });
      return {
        success: true,
        message: "Automation started successfully",
        taskRunId: response.taskRunId
      };
    } catch (error) {
      log.error('💥', 'Failed to start automation:', error);
      throw error;
    }
  }

  /**
   * Stop a running automation
   * @param {string} taskRunId - The task run ID to stop
   * @returns {Promise<object>}
   */
  async stopAutomation(taskRunId) {
    log.info('⏹️', `Stopping task run ${taskRunId.slice(0, 8)}...`);

    try {
      await this.request(`/task-runs/${taskRunId}/stop`, {
        method: 'POST'
      });

      log.success('✅', 'Automation stopped');
      return {
        success: true,
        message: "Automation stopped successfully"
      };
    } catch (error) {
      // Handle case where task already completed/failed/stopped before stop was clicked
      // This isn't really an error from user perspective - the task is done
      if (error.message?.startsWith('Task run already')) {
        log.info('ℹ️', `Task already finished: ${error.message}`);
        return {
          success: true,
          alreadyFinished: true,
          message: error.message
        };
      }
      log.error('💥', 'Failed to stop automation:', error);
      throw error;
    }
  }

  /**
   * Resume a failed task run from its last checkpoint
   * @param {string} taskRunId - The failed task run ID to resume
   * @returns {Promise<{ message: string, newRunId: string, originalRunId: string }>}
   */
  async resumeTaskRun(taskRunId) {
    log.info('▶️', `Resuming task run ${taskRunId.slice(0, 8)}...`);
    const result = await this.request(`/task-runs/${taskRunId}/resume`, { method: 'POST' });
    log.success('✅', 'Resume task run created', result);
    return result;
  }

  /**
   * Delete a task (soft delete)
   * - Fails if task has running or pending task runs
   * - Automatically disables schedule
   */
  async deleteTask(taskId) {
    log.info('🗑️', `Deleting task ${taskId.slice(0, 8)}...`);

    try {
      const response = await this.request(`/tasks/${taskId}`, {
        method: 'DELETE'
      });

      log.success('✅', 'Task deleted');
      return response;
    } catch (error) {
      log.error('💥', 'Failed to delete task:', error);
      throw error;
    }
  }

  /**
   * Delete multiple tasks in bulk (soft delete)
   * @param {string[]} taskIds - Array of task IDs to delete (max 100)
   * @returns {Promise<{ message: string, deleted: string[], blocked: Array<{id: string, error: string}>, notFound: Array<{id: string, error: string}> }>}
   */
  async deleteTasksBulk(taskIds) {
    log.info('🗑️', `Deleting ${taskIds.length} tasks in bulk...`);

    try {
      const response = await this.request('/tasks/delete', {
        method: 'POST',
        body: JSON.stringify({ ids: taskIds })
      });

      log.success('✅', `Bulk delete completed: ${response.deleted?.length || 0} deleted`);
      return response;
    } catch (error) {
      log.error('💥', 'Failed to bulk delete tasks:', error);
      throw error;
    }
  }

  /**
   * Get a specific task by ID
   */
  async getTask(taskId) {
    log.info('📋', `Fetching task ${taskId.slice(0, 8)}...`);

    try {
      const response = await this.request(`/tasks/${taskId}`);
      log.success('✅', 'Task fetched');
      return response;
    } catch (error) {
      log.error('💥', 'Failed to fetch task:', error);
      throw error;
    }
  }

  /**
   * Get available proxy locations (cached for 24h when fetching all with cities)
   */
  async getProxyLocations({ type = "all", includeCities = false, country = null } = {}) {
    const useCache = type === "all" && includeCities && !country;
    const cacheKey = "cloudProxyLocationsCache";
    const cacheTTL = 24 * 60 * 60 * 1000;

    let staleCache = null;

    // Try to read cache (keep as fallback even if expired)
    if (useCache) {
      try {
        const str = localStorage.getItem(cacheKey);
        const cached = str ? JSON.parse(str) : null;
        // Validate cache structure
        if (cached?.data && typeof cached.timestamp === 'number') {
          if (Date.now() - cached.timestamp < cacheTTL) {
            return cached.data;
          }
          staleCache = cached.data; // Keep expired cache as fallback
        }
      } catch (err) {
        log.error('💥', 'Failed to read proxy locations cache:', err);
      }
    }

    // Try to fetch from API
    try {
      const params = new URLSearchParams({ type, includeCities: includeCities.toString() });
      if (country) params.set("country", country);
      const data = await this.request(`/proxies/locations?${params}`);

      // Validate response structure before caching
      if (useCache && data && typeof data === 'object') {
        try {
          const cacheEntry = { data, timestamp: Date.now() };
          localStorage.setItem(cacheKey, JSON.stringify(cacheEntry));
        } catch (err) {
          log.error('💥', 'Failed to cache proxy locations:', err);
        }
      }

      return data;
    } catch (err) {
      // API failed - return stale cache if available
      if (staleCache) {
        log.warn('⚠️', 'API failed, returning stale cache:', err.message);
        return staleCache;
      }
      throw err;
    }
  }

  /**
   * Update a task with partial updates
   */
  async updateTask(taskId, updates) {
    log.info('✏️', `Updating task ${taskId.slice(0, 8)}...`, { fields: Object.keys(updates) });

    try {
      const response = await this.request(`/tasks/${taskId}`, {
        method: 'PATCH',
        body: JSON.stringify(updates)
      });
      log.success('✅', 'Task updated');
      return response;
    } catch (error) {
      log.error('💥', 'Failed to update task:', error);
      throw error;
    }
  }

  /**
   * Update automation schedule
   */
  async updateAutomationSchedule(automationId, schedule) {
    log.info('📅', `Updating schedule for ${automationId.slice(0, 8)}...`, { enabled: schedule.enabled });

    try {
      const payload = {
        schedule: {
          enabled: schedule.enabled
        }
      };

      // Only include cronExpression if enabling the schedule.
      // A caller-supplied cronExpression (custom schedule) wins; otherwise
      // fall back to mapping a preset interval to its cron equivalent.
      if (schedule.enabled) {
        const cron = schedule.cronExpression || (schedule.interval ? this.intervalToCron(schedule.interval) : null);
        if (cron) {
          payload.schedule.cronExpression = cron;
        }
      }

      await this.request(`/tasks/${automationId}`, {
        method: 'PATCH',
        body: JSON.stringify(payload)
      });

      log.success('✅', 'Schedule updated');
      return {
        success: true,
        automationId,
        schedule: {
          ...schedule,
          lastUpdated: new Date()
        }
      };
    } catch (error) {
      log.error('💥', 'Failed to update schedule:', error);
      throw error;
    }
  }

  intervalToCron(minutes) {
    if (!minutes || minutes <= 0) return '0 0 * * *';
    const entry = CloudAPIClient.SCHEDULE_MAP.find(e => e.interval === minutes);
    return entry ? entry.cron : '0 0 * * *';
  }

  /**
   * Get task run by ID (lightweight status check)
   * @param {string} taskRunId - Task run UUID
   * @returns {Promise<object>} Task run with status, timestamps, etc.
   */
  async getTaskRun(taskRunId) {
    try {
      const response = await this.request(`/task-runs/${taskRunId}`);
      return response.run || response;
    } catch (error) {
      log.error('💥', 'Failed to fetch task run:', error);
      throw error;
    }
  }

  /**
   * Get detailed information about a specific task run
   */
  async getAutomationRunDetails(runId) {
    try {
      const response = await this.request(`/task-runs/${runId}`);
      const run = response.run || response;

      // Extract journal and screenshots from run_metadata or result
      const journal = run.run_metadata?.journal || run.result?.journal || [];
      const screenshotUrls = run.run_metadata?.screenshotUrls || run.result?.screenshotUrls || [];
      const journalSummary = run.run_metadata?.journalSummary || null;
      const network = run.run_metadata?.network || run.result?.network || null;
      const creditsConsumed = run.credits_consumed != null
        ? Number(run.credits_consumed)
        : (run.run_metadata?.creditsConsumed ?? run.result?.creditsConsumed ?? null);
      const extraction = run.run_metadata?.extraction || null;
      const catalogue = run.run_metadata?.catalogue || null;
      const timingReport = run.run_metadata?.timingReport || run.result?.timingReport || null;
      const googleSheetsExport = run.run_metadata?.googleSheetsExport || run.result?.googleSheetsExport || null;

      // Extract URL processing stats and recent activity for real-time progress
      const urlStats = run.run_metadata?.stats || null;
      const recentActivity = run.run_metadata?.recentActivity || [];
      const batchNumber = run.run_metadata?.batchNumber || null;
      const avgTabs = run.run_metadata?.avgTabs ?? null;
      const blockSummary = run.run_metadata?.blockSummary || null;
      const recoverySummary = run.run_metadata?.recoverySummary || null;
      const webhookExport = run.run_metadata?.webhookExport || run.result?.webhookExport || null;
      const rowCount = extraction?.rowCount || run.run_metadata?.rowCount || run.item_count || 0;

      // Sub-page scan progress (contact extraction). The per-batch metadata carries a live
      // `subPageScan` object; the final write instead reports the totals top-level, so fall
      // back to those to keep the card accurate after completion.
      const liveSubPageScan = run.run_metadata?.subPageScan || null;
      const subPageScan = liveSubPageScan
        ? {
            ...liveSubPageScan,
            pagesScannedTotal: liveSubPageScan.pagesScannedTotal ?? run.run_metadata?.pagesScannedTotal ?? 0,
            emailsFound: liveSubPageScan.emailsFound ?? run.run_metadata?.emailsFound ?? 0,
            socialFound: liveSubPageScan.socialFound ?? run.run_metadata?.socialFound ?? 0,
          }
        : (run.run_metadata?.pagesScannedTotal != null
            ? {
                pagesScannedTotal: run.run_metadata.pagesScannedTotal,
                emailsFound: run.run_metadata.emailsFound ?? 0,
                socialFound: run.run_metadata.socialFound ?? 0,
                currentUrl: null,
                enabled: false,
                maxDepth: null,
              }
            : null);

      // Transform backend response to match frontend expectations (convert UTC to local time)
      return {
        runId: run.id,
        status: this.mapRunStatusToTimelineStatus(run.status),
        startTime: this.toLocalDate(run.started_at),
        endTime: this.toLocalDate(run.completed_at),
        message: this.generateRunMessage(run),
        extractedData: run.result ? (typeof run.result === 'string' ? JSON.parse(run.result) : run.result) : null,
        journal: journal.map(step => ({
          ...step,
          timestamp: new Date(step.timestamp)
        })),
        journalSummary,
        screenshotUrls,
        network,
        creditsConsumed,
        extraction,
        catalogue,
        timingReport,
        urlStats,
        recentActivity,
        batchNumber,
        avgTabs,
        blockSummary,
        recoverySummary,
        subPageScan,
        googleSheetsExport,
        webhookExport,
        rowCount,
        queuedAt: this.toLocalDate(run.queued_at || run.created_at),
        error: run.error || run.result?.error || null,
        stoppedReason: run.run_metadata?.stoppedReason || run.result?.stoppedReason || null,
        tableId: run.table_id || null
      };
    } catch (error) {
      log.error('💥', 'Failed to fetch run details:', error);
      throw error;
    }
  }

  /**
   * Get task run history for a specific task
   * @param {string} taskId - Task ID
   * @param {Object} options - { limit, offset, status, triggerType }
   * @returns {Promise<{ runs: [], total: number }>}
   */
  async getTaskRunHistory(taskId, options = {}) {
    try {
      const params = new URLSearchParams();
      params.append('task_id', taskId);

      if (options.limit) params.append('limit', options.limit);
      if (options.offset) params.append('offset', options.offset);
      if (options.status) params.append('status', options.status);
      if (options.triggerType) params.append('trigger_type', options.triggerType);

      const response = await this.request(`/task-runs?${params.toString()}`);
      return response;
    } catch (error) {
      log.error('💥', 'Failed to fetch task run history:', error);
      throw error;
    }
  }

  /**
   * Get all task runs across all tasks for the workspace
   * @param {Object} options - { limit, offset, status, triggerType, fromDate, toDate }
   * @returns {Promise<{ runs: [], total: number, limit: number, offset: number }>}
   */
  async getAllTaskRuns(options = {}) {
    try {
      const params = new URLSearchParams();

      if (options.limit) params.append('limit', options.limit);
      if (options.offset) params.append('offset', options.offset);
      if (options.status) params.append('status', options.status);
      if (options.triggerType) params.append('trigger_type', options.triggerType);
      if (options.fromDate) params.append('from_date', options.fromDate);
      if (options.toDate) params.append('to_date', options.toDate);

      const queryString = params.toString();
      const endpoint = queryString ? `/task-runs?${queryString}` : '/task-runs';

      const response = await this.request(endpoint);
      return response;
    } catch (error) {
      log.error('💥', 'Failed to fetch all task runs:', error);
      throw error;
    }
  }

  buildAuthPayload(options = {}) {
    if (options.authSource !== 'chrome_extension') {
      return {};
    }

    return {
      authSource: 'chrome_extension',
      deviceId: options.deviceId || undefined,
      deviceName: options.deviceName || undefined,
      extensionVersion: options.extensionVersion || undefined,
    };
  }

  setExtensionSessionCookie(extensionToken, maxAgeSeconds = this.extensionSessionCookieMaxAgeSeconds) {
    if (typeof document === 'undefined' || !extensionToken) {
      return;
    }

    const secureFlag = typeof window !== 'undefined' && window.location?.protocol === 'https:' ? '; Secure' : '';
    document.cookie =
      `${this.extensionSessionCookieName}=${encodeURIComponent(extensionToken)}; Max-Age=${maxAgeSeconds}; Path=/; SameSite=Lax${secureFlag}`;
  }

  clearExtensionSessionCookie() {
    if (typeof document === 'undefined') {
      return;
    }

    const secureFlag = typeof window !== 'undefined' && window.location?.protocol === 'https:' ? '; Secure' : '';
    document.cookie = `${this.extensionSessionCookieName}=; Max-Age=0; Path=/; SameSite=Lax${secureFlag}`;
  }

  async maybePrepareExtensionSessionCookie(options = {}) {
    if (options.authSource !== 'chrome_extension') {
      return null;
    }

    const sessionTokenResponse = await this.request('/auth/extension/session-token', {
      method: 'POST',
      body: JSON.stringify({
        deviceId: options.deviceId || undefined,
        deviceName: options.deviceName || undefined,
        extensionVersion: options.extensionVersion || undefined,
      })
    });

    if (!sessionTokenResponse?.token) {
      throw new Error('Failed to prepare extension session token');
    }

    this.setExtensionSessionCookie(sessionTokenResponse.token, this.extensionSessionCookieMaxAgeSeconds);
    return sessionTokenResponse;
  }

  /**
   * Authentication methods
   */
  async requestMagicLink(email, options = {}) {
    log.info('🔐', 'Requesting magic link...');

    try {
      await this.clearWorkspaceId();
      this.workspaces = [];

      const response = await this.request('/auth/magic-link', {
        method: 'POST',
        body: JSON.stringify({
          email,
          ...this.buildAuthPayload(options),
        })
      });

      log.success('✅', 'Magic link sent');
      return response;
    } catch (error) {
      log.error('💥', 'Magic link request failed:', error);
      throw error;
    }
  }

  async verifyMagicLink(token, options = {}) {
    log.info('🔐', 'Verifying magic link...');

    try {
      await this.clearWorkspaceId();
      this.workspaces = [];

      const response = await this.request('/auth/magic-link/verify', {
        method: 'POST',
        body: JSON.stringify({
          token,
          ...this.buildAuthPayload(options),
        })
      });

      if (response.token) {
        await this.setAuthToken(response.token);
        await this.maybePrepareExtensionSessionCookie(options);
      }

      log.success('✅', 'Magic link verified');
      return response;
    } catch (error) {
      log.error('💥', 'Magic link verification failed:', error);
      throw error;
    }
  }

  async verifyLoginCode(email, code, options = {}) {
    log.info('🔐', 'Verifying login code...');

    try {
      await this.clearWorkspaceId();
      this.workspaces = [];

      const response = await this.request('/auth/login-code/verify', {
        method: 'POST',
        body: JSON.stringify({
          email,
          code,
          ...this.buildAuthPayload(options),
        })
      });

      if (response.token) {
        await this.setAuthToken(response.token);
        await this.maybePrepareExtensionSessionCookie(options);
      }

      log.success('✅', 'Login code verified');
      return response;
    } catch (error) {
      log.error('💥', 'Login code verification failed:', error);
      throw error;
    }
  }

  async login(email, password, options = {}) {
    log.info('🔐', 'Logging in...');

    try {
      // Clear any stale data from previous sessions before logging in
      await this.clearWorkspaceId();
      this.workspaces = [];

      const response = await this.request('/auth/login', {
        method: 'POST',
        body: JSON.stringify({
          email,
          password,
          ...this.buildAuthPayload(options),
        })
      });

      if (response.token) {
        await this.setAuthToken(response.token);
        await this.maybePrepareExtensionSessionCookie(options);
      }

      log.success('✅', 'Login successful');
      return response;
    } catch (error) {
      log.error('💥', 'Login failed:', error);
      throw error;
    }
  }

  async loginWithGoogle(code, options = {}) {
    log.info('🔐', 'Logging in with Google...');

    try {
      await this.clearWorkspaceId();
      this.workspaces = [];

      const response = await this.request('/auth/google', {
        method: 'POST',
        body: JSON.stringify({
          code,
          ...this.buildAuthPayload(options),
        })
      });

      if (response.token) {
        await this.setAuthToken(response.token);
        await this.maybePrepareExtensionSessionCookie(options);
      }

      log.success('✅', 'Google login successful');
      return response;
    } catch (error) {
      log.error('💥', 'Google login failed:', error);
      throw error;
    }
  }

  async register(email, password, name, options = {}) {
    log.info('🔐', 'Registering new account...');

    try {
      // Clear any stale data from previous sessions before registering
      await this.clearWorkspaceId();
      this.workspaces = [];

      const response = await this.request('/auth/register', {
        method: 'POST',
        body: JSON.stringify({
          email,
          password,
          name,
          ...this.buildAuthPayload(options),
        })
      });

      if (response.token) {
        await this.setAuthToken(response.token);
        await this.maybePrepareExtensionSessionCookie(options);
      }

      log.success('✅', 'Registration successful');
      return response;
    } catch (error) {
      log.error('💥', 'Registration failed:', error);
      throw error;
    }
  }

  async logout() {
    log.info('🔐', 'Logging out...');

    try {
      // Call backend logout to invalidate session
      if (this.authToken) {
        await this.request('/auth/logout', {
          method: 'POST'
        });
      }
      log.success('✅', 'Logged out');
    } catch (error) {
      log.error('💥', 'Backend logout failed:', error);
      // Continue with local logout even if backend fails
    } finally {
      this.clearExtensionSessionCookie();
      await this.clearAuthToken();
      await this.clearWorkspaceId();
      this.workspaces = [];
    }
  }

  isAuthenticated() {
    return !!this.authToken;
  }

  async updateProfile(data) {
    return this.request('/users/profile', {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  // =============================================================================
  // WORKSPACE METHODS
  // =============================================================================

  /**
   * Get user's workspaces
   */
  async getWorkspaces({ viewAsUserId } = {}) {
    log.info('🏢', 'Fetching workspaces...');

    try {
      const options = viewAsUserId ? { headers: { 'X-View-As-User-Id': viewAsUserId } } : {};
      const response = await this.request('/workspaces', options);
      this.workspaces = response.workspaces || [];

      // If no workspace is selected, use the first one (or the default/owned workspace)
      if (!this.workspaceId && this.workspaces.length > 0) {
        // Find owned workspace or use first one
        const ownedWorkspace = this.workspaces.find(w => w.role === 'owner');
        const defaultWorkspace = ownedWorkspace || this.workspaces[0];
        await this.setWorkspaceId(defaultWorkspace.id);
      }

      log.success('✅', `Loaded ${this.workspaces.length} workspaces`);
      return this.workspaces;
    } catch (error) {
      log.error('💥', 'Failed to fetch workspaces:', error);
      throw error;
    }
  }

  /**
   * Create a new workspace
   */
  async createWorkspace(name, metadata = {}) {
    log.info('🏢', 'Creating workspace...', { name });

    try {
      const response = await this.request('/workspaces', {
        method: 'POST',
        body: JSON.stringify({ name, metadata })
      });

      // Refresh workspace list
      await this.getWorkspaces();

      log.success('✅', 'Workspace created');
      return response.workspace;
    } catch (error) {
      log.error('💥', 'Failed to create workspace:', error);
      throw error;
    }
  }

  /**
   * Update workspace details, e.g. rename (owner only)
   *
   * Note: request() always stamps X-Workspace-Id with the *active* workspace,
   * and the backend resolves that header ahead of the :workspaceId path param,
   * so this can only reliably target the currently selected workspace.
   *
   * The caller owns refreshing UI state afterwards (AuthContext.refreshWorkspaces),
   * since the response is shaped like a raw workspace row and lacks the role /
   * features / member_count fields the workspace list carries.
   */
  async updateWorkspace(workspaceId, updates) {
    log.info('🏢', 'Updating workspace...', { workspaceId, ...updates });

    try {
      const response = await this.request(`/workspaces/${workspaceId}`, {
        method: 'PATCH',
        body: JSON.stringify(updates)
      });

      log.success('✅', 'Workspace updated');
      return response.workspace;
    } catch (error) {
      log.error('💥', 'Failed to update workspace:', error);
      throw error;
    }
  }

  /**
   * Switch to a different workspace
   */
  async switchWorkspace(workspaceId) {
    await this.setWorkspaceId(workspaceId);
    return workspaceId;
  }

  /**
   * Get workspace details including members
   */
  async getWorkspaceDetails(workspaceId) {
    try {
      const response = await this.request(`/workspaces/${workspaceId}`);
      return response;
    } catch (error) {
      log.error('💥', 'Failed to fetch workspace details:', error);
      throw error;
    }
  }

  /**
   * Invite member to workspace (owner only)
   */
  async inviteWorkspaceMember(workspaceId, email) {
    log.info('👥', 'Inviting member...', { email });

    try {
      const response = await this.request(`/workspaces/${workspaceId}/members`, {
        method: 'POST',
        body: JSON.stringify({ email })
      });
      log.success('✅', 'Member invited');
      return response;
    } catch (error) {
      log.error('💥', 'Failed to invite member:', error);
      throw error;
    }
  }

  /**
   * Get pending workspace invitations
   */
  async getPendingInvitations() {
    try {
      const response = await this.request('/workspaces/invitations');
      return response.invitations || [];
    } catch (error) {
      log.error('💥', 'Failed to fetch invitations:', error);
      throw error;
    }
  }

  /**
   * Accept workspace invitation
   */
  async acceptWorkspaceInvitation(token) {
    log.info('👥', 'Accepting workspace invitation...');

    try {
      const response = await this.request('/workspaces/invitations/accept', {
        method: 'POST',
        body: JSON.stringify({ token })
      });

      // Refresh workspace list
      await this.getWorkspaces();

      log.success('✅', 'Invitation accepted');
      return response.workspace;
    } catch (error) {
      log.error('💥', 'Failed to accept invitation:', error);
      throw error;
    }
  }

  /**
   * Accept workspace invitation by ID (in-app flow)
   */
  async acceptInvitationById(invitationId) {
    log.info('👥', 'Accepting invitation by id...');
    const response = await this.request(`/workspaces/invitations/${invitationId}/accept`, {
      method: 'POST',
    });
    return response.workspace;
  }

  /**
   * Decline workspace invitation by ID (in-app flow)
   */
  async declineInvitationById(invitationId) {
    return this.request(`/workspaces/invitations/${invitationId}/decline`, {
      method: 'POST',
    });
  }

  /**
   * Cancel a pending invitation (owner only)
   */
  async cancelWorkspaceInvitation(workspaceId, invitationId) {
    return this.request(`/workspaces/${workspaceId}/invitations/${invitationId}`, {
      method: 'DELETE',
    });
  }

  /**
   * Get all members of a workspace (including pending invitations)
   */
  async getWorkspaceMembers(workspaceId) {
    const response = await this.request(`/workspaces/${workspaceId}/members`);
    return response.members || [];
  }

  /**
   * Remove a member from a workspace (owner only)
   */
  async removeWorkspaceMember(workspaceId, userId) {
    return this.request(`/workspaces/${workspaceId}/members/${userId}`, {
      method: 'DELETE',
    });
  }

  /**
   * Leave a workspace (members only; owners cannot leave)
   */
  async leaveWorkspace(workspaceId) {
    return this.request(`/workspaces/${workspaceId}/leave`, {
      method: 'POST',
    });
  }

  /**
   * Persist the user's default workspace on the server
   */
  async setDefaultWorkspace(workspaceId) {
    return this.request('/users/me/preferences', {
      method: 'PATCH',
      body: JSON.stringify({ default_workspace_id: workspaceId }),
    });
  }

  // =============================================================================
  // CREDITS & SUBSCRIPTION METHODS
  // =============================================================================

  /**
   * Get current credits balance for the workspace
   * @returns {Promise<{ balance: { available, consumed, total, expiring_soon }, active_allocations: [] }>}
   */
  async getCreditsBalance() {
    try {
      const response = await this.request('/subscription/credits');
      return response;
    } catch (error) {
      log.error('💥', 'Failed to fetch credits balance:', error);
      throw error;
    }
  }

  async getCreditUsage(days = 30) {
    try {
      const response = await this.request(`/subscription/credits/usage?days=${days}`);
      return response;
    } catch (error) {
      log.error('💥', 'Failed to fetch credit usage:', error);
      throw error;
    }
  }

  // =============================================================================
  // TABLE DATA METHODS
  // =============================================================================

  /**
   * Get tables grouped by source task (for Database view)
   * @param {Object} options - { limit, page, search, domain, sortBy, sortOrder, taskType, minRows, dateFrom, dateTo }
   * @returns {Promise<{ data: [], pagination: {} }>}
   */
  async getTablesGrouped(options = {}) {
    const { limit = 20, page = 1, search, domain, sortBy, sortOrder, taskType, minRows, dateFrom, dateTo } = options;
    const params = new URLSearchParams();
    params.append('limit', limit);
    params.append('page', page);
    if (search) params.append('search', search);
    if (domain) params.append('domain', domain);
    if (sortBy) params.append('sortBy', sortBy);
    if (sortOrder) params.append('sortOrder', sortOrder);
    if (taskType) params.append('taskType', taskType);
    if (minRows !== undefined && minRows !== '') params.append('minRows', minRows);
    if (dateFrom) params.append('dateFrom', dateFrom);
    if (dateTo) params.append('dateTo', dateTo);

    const response = await this.request(`/tables/grouped?${params.toString()}`);
    return response;
  }

  /**
   * Get distinct domains for the domain filter combobox
   * @param {string} search - Optional search term
   * @returns {Promise<{ data: Array<{ domain: string, count: number }> }>}
   */
  async getGroupedDomains(search = '') {
    const params = new URLSearchParams();
    if (search) params.append('search', search);
    const response = await this.request(`/tables/grouped/domains?${params.toString()}`);
    return response;
  }

  /**
   * Get all tables for a specific task (historical extractions)
   * @param {string} taskId - Task UUID
   * @param {Object} options - { limit, page, sortOrder }
   * @returns {Promise<{ data: [], pagination: {} }>}
   */
  async getTablesByTaskId(taskId, options = {}) {
    const { limit = 50, page = 1, sortOrder = 'desc' } = options;
    const params = new URLSearchParams();
    params.append('limit', limit);
    params.append('page', page);
    params.append('sortOrder', sortOrder);

    const response = await this.request(`/tables/by-task/${taskId}?${params.toString()}`);
    return response;
  }

  /**
   * Get table with paginated rows
   * @param {string} tableId - Table UUID
   * @param {Object} options - { page, limit, sortBy, sortOrder, search }
   * @returns {Promise<{ id, name, columns, metadata, rowCount, rows[], pagination }>}
   */
  async getTableWithRows(tableId, options = {}) {
    log.info('📋', `Fetching table ${tableId.slice(0, 8)}...`, options);

    try {
      const params = new URLSearchParams();

      if (options.page) params.append('page', options.page);
      if (options.limit) params.append('limit', options.limit);
      if (options.sortBy) params.append('sortBy', options.sortBy);
      if (options.sortOrder) params.append('sortOrder', options.sortOrder);
      if (options.search && options.search.trim()) params.append('search', options.search.trim());

      const queryString = params.toString();
      const endpoint = queryString
        ? `/tables/${tableId}?${queryString}`
        : `/tables/${tableId}`;

      const response = await this.request(endpoint);
      const table = response.data;

      log.success('✅', `Table loaded: ${table.rowCount} rows, ${table.columns?.length || 0} columns`, table);

      return table;
    } catch (error) {
      log.error('💥', 'Failed to fetch table with rows:', error);
      throw error;
    }
  }

  /**
   * Update column properties (name and/or type)
   * @param {string} tableId - Table UUID
   * @param {string} columnId - Column ID to update
   * @param {Object} updates - { name?: string, type?: string }
   * @returns {Promise<{success: boolean, data: {column: Object}}>}
   */
  async updateColumn(tableId, columnId, updates) {
    log.info('✏️', `Updating column ${columnId} in table ${tableId.slice(0, 8)}...`, updates);

    try {
      const response = await this.request(`/tables/${tableId}/columns/${columnId}`, {
        method: 'PATCH',
        body: JSON.stringify(updates)
      });
      log.success('✅', 'Column updated');
      return response;
    } catch (error) {
      log.error('💥', 'Failed to update column:', error);
      throw error;
    }
  }

  /**
   * Delete a column from a table
   * @param {string} tableId - Table UUID
   * @param {string} columnId - Column ID to delete
   * @returns {Promise<{success: boolean, data: {deletedColumnId: string, rowsAffected: number}}>}
   */
  async deleteColumn(tableId, columnId) {
    log.info('🗑️', `Deleting column ${columnId} from table ${tableId.slice(0, 8)}...`);

    try {
      const response = await this.request(`/tables/${tableId}/columns/${columnId}`, {
        method: 'DELETE'
      });
      log.success('✅', 'Column deleted');
      return response;
    } catch (error) {
      log.error('💥', 'Failed to delete column:', error);
      throw error;
    }
  }

  /**
   * Delete rows from a table
   * @param {string} tableId - Table UUID
   * @param {string[]} rowIds - Row IDs to delete
   * @returns {Promise<{success: boolean, data: {deletedCount: number, remainingRows: number}}>}
   */
  async deleteRows(tableId, rowIds) {
    log.info('🗑️', `Deleting ${rowIds.length} row(s) from table ${tableId.slice(0, 8)}...`);

    try {
      const response = await this.request(`/tables/${tableId}/rows/delete`, {
        method: 'POST',
        body: JSON.stringify({ rowIds })
      });
      log.success('✅', 'Rows deleted');
      return response;
    } catch (error) {
      log.error('💥', 'Failed to delete rows:', error);
      throw error;
    }
  }

  /**
   * Get export URL with authentication token
   * @param {string} tableId - Table UUID
   * @param {string} format - 'csv' | 'excel' | 'json'
   * @param {Array<string>} columnIds - Optional column IDs to export
   * @returns {string} Full authenticated URL
   */
  getExportUrl(tableId, format, columnIds = null) {
    const params = new URLSearchParams();
    params.append('format', format);
    if (columnIds && columnIds.length > 0) {
      params.append('columns', columnIds.join(','));
    }
    return `${this.baseURL}/api/tables/${tableId}/export?${params.toString()}`;
  }

  /**
   * Download export file (triggers browser download)
   * @param {string} tableId - Table UUID
   * @param {string} format - 'csv' | 'excel' | 'json'
   * @param {Array<string>} columnIds - Optional column IDs to export
   * @param {string} filename - Optional custom filename
   */
  async downloadExport(tableId, format, columnIds = null, filename = null) {
    try {
      const url = this.getExportUrl(tableId, format, columnIds);

      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${this.authToken}`,
          // Scope to the active workspace like every other API call — without this the export
          // route falls back to the user's default workspace and 404s any table that lives in
          // a different one (e.g. MCP-authored Shopify catalogues).
          'X-Workspace-Id': this.workspaceId
        }
      });

      if (response.status === 401) {
        // Token expired or invalid - ONLY clear on actual 401 response
        await this.clearAuthToken();
        throw new Error('Authentication required. Please log in again.');
      }

      if (!response.ok) {
        const error = await response.json().catch(() => ({ message: 'Export failed' }));
        // The export route returns { success:false, error } (e.g. the Shopify-CSV gate messages:
        // not-a-Shopify-table / archived / too-large), so prefer `error` before the generic fallback.
        throw new Error(error.error || error.message || `Export failed: ${response.statusText}`);
      }

      // Get filename from Content-Disposition header or use provided/default
      let downloadFilename = filename;
      if (!downloadFilename) {
        const contentDisposition = response.headers.get('Content-Disposition');
        if (contentDisposition) {
          const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
          if (filenameMatch && filenameMatch[1]) {
            downloadFilename = filenameMatch[1].replace(/['"]/g, '');
          }
        }
      }

      // Default filename if none found
      if (!downloadFilename) {
        const extension = format === 'excel' ? 'xlsx' : format;
        downloadFilename = `table-export-${tableId}.${extension}`;
      }

      // Create blob and trigger download
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = downloadFilename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Clean up blob URL
      setTimeout(() => URL.revokeObjectURL(blobUrl), 100);

      log.success('💾', `Downloaded ${downloadFilename}`);
      return {
        success: true,
        filename: downloadFilename
      };
    } catch (error) {
      log.error('💥', 'Failed to download export:', error);
      throw error;
    }
  }

  // ==================== Image Download Methods ====================

  /**
   * Get all image URLs from a table (bypasses pagination)
   * @param {string} tableId - Table UUID
   * @param {Array<string>} columnIds - Optional array of image column IDs
   * @returns {Promise<{urls: Array, total: number, columns: Array}>}
   */
  async getImageUrls(tableId, columnIds = null) {
    try {
      const params = new URLSearchParams();
      if (columnIds && columnIds.length > 0) {
        params.append('columns', columnIds.join(','));
      }

      const queryString = params.toString();
      const endpoint = queryString
        ? `/tables/${tableId}/images?${queryString}`
        : `/tables/${tableId}/images`;

      const response = await this.request(endpoint);
      return {
        urls: response.urls || [],
        total: response.total || 0,
        columns: response.columns || []
      };
    } catch (error) {
      log.error('💥', 'Failed to get image URLs:', error);
      throw error;
    }
  }

  /**
   * Get URL for image ZIP download
   * @param {string} tableId - Table UUID
   * @param {Array<string>} columnIds - Image column IDs
   * @param {string} namingTemplate - Filename template
   * @returns {string} Full authenticated URL
   */
  getImageDownloadUrl(tableId, columnIds = null, namingTemplate = '{column_name}_{index}') {
    const params = new URLSearchParams();
    if (columnIds && columnIds.length > 0) {
      params.append('columns', columnIds.join(','));
    }
    params.append('naming', namingTemplate);
    return `${this.baseURL}/api/tables/${tableId}/images/download?${params.toString()}`;
  }

  /**
   * Download images as ZIP file from backend
   * @param {string} tableId - Table UUID
   * @param {Array<string>} columnIds - Image column IDs
   * @param {string} namingTemplate - Filename template
   * @returns {Promise<{success: boolean, filename: string}>}
   */
  async downloadImagesZip(tableId, columnIds = null, namingTemplate = '{column_name}_{index}') {
    try {
      const url = this.getImageDownloadUrl(tableId, columnIds, namingTemplate);

      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${this.authToken}`,
          'X-Workspace-Id': this.workspaceId
        }
      });

      if (response.status === 401) {
        await this.clearAuthToken();
        throw new Error('Authentication required. Please log in again.');
      }

      if (!response.ok) {
        const error = await response.json().catch(() => ({ message: 'Download failed' }));
        throw new Error(error.message || error.error || `Download failed: ${response.statusText}`);
      }

      // Get filename from Content-Disposition header
      let filename = 'images.zip';
      const contentDisposition = response.headers.get('Content-Disposition');
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1].replace(/['"]/g, '');
        }
      }

      // Create blob and trigger download
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      setTimeout(() => URL.revokeObjectURL(blobUrl), 100);

      log.success('🖼️', `Downloaded ${filename}`);
      return {
        success: true,
        filename
      };
    } catch (error) {
      log.error('💥', 'Failed to download images ZIP:', error);
      throw error;
    }
  }

  /**
   * Lightweight sync check - returns summary stats + per-task fingerprints
   */
  async getDashboardSync(options = {}) {
    const { scheduledOnly = false, filters = {} } = options;
    const params = new URLSearchParams();
    if (scheduledOnly) params.append('scheduled_only', 'true');
    if (filters.status?.length) params.append('status', filters.status.join(','));
    if (filters.taskType) params.append('task_type', filters.taskType);

    return this.request(`/dashboard/sync?${params.toString()}`);
  }

  /**
   * Fetch full task data for specific task IDs (for surgical updates)
   */
  async getAutomationsByIds(taskIds, options = {}) {
    const { includeRuns = 100 } = options;

    const response = await this.request('/dashboard/tasks', {
      method: 'POST',
      body: JSON.stringify({ task_ids: taskIds, include_runs: includeRuns }),
    });

    return {
      automations: response.tasks.map(task => this.transformTaskToAutomation(task)),
    };
  }

  // ============================================================================
  // Support
  // ============================================================================

  /**
   * Upload request — sends FormData instead of JSON
   */
  async uploadRequest(endpoint, formData) {
    const url = `${this.baseURL}/api${endpoint}`;
    const headers = {};

    if (this.authToken) {
      headers['Authorization'] = `Bearer ${this.authToken}`;
    }
    if (this.workspaceId) {
      headers['X-Workspace-Id'] = this.workspaceId;
    }
    if (this.viewAsUserId) {
      headers['X-View-As-User-Id'] = this.viewAsUserId;
    }

    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: formData,
    });

    if (response.status === 401) {
      await this.clearAuthToken();
      throw new Error('Authentication required. Please log in again.');
    }

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `Request failed (${response.status})`);
    }

    return await response.json();
  }

  async createSupportTicket({ category, subject, message, screenshots = [], contactEmail }) {
    const formData = new FormData();
    formData.append('category', category);
    formData.append('subject', subject);
    formData.append('message', message);
    if (contactEmail) formData.append('contact_email', contactEmail);
    screenshots.forEach(file => formData.append('screenshots', file));
    return this.uploadRequest('/support', formData);
  }

  async getSupportTickets(options = {}) {
    const params = new URLSearchParams();
    if (options.status) params.append('status', options.status);
    if (options.limit) params.append('limit', options.limit);
    if (options.offset) params.append('offset', options.offset);
    const qs = params.toString();
    return this.request(`/support${qs ? `?${qs}` : ''}`);
  }

  async getSupportTicket(ticketId) {
    return this.request(`/support/${ticketId}`);
  }

  async addSupportReply(ticketId, message, screenshots = []) {
    const formData = new FormData();
    formData.append('message', message);
    screenshots.forEach(file => formData.append('screenshots', file));
    return this.uploadRequest(`/support/${ticketId}/reply`, formData);
  }

  async getSupportUnreadCount() {
    return this.request('/support/unread-count');
  }

  async markSupportTicketRead(ticketId) {
    return this.request(`/support/${ticketId}/read`, { method: 'POST' });
  }

  getSupportAttachmentUrl(ticketId, index) {
    const token = this.authToken || '';
    const wsId = this.workspaceId || '';
    return `${this.baseURL}/api/support/${ticketId}/attachment/${index}?token=${encodeURIComponent(token)}&workspace=${encodeURIComponent(wsId)}`;
  }

  getSupportReplyAttachmentUrl(ticketId, replyId, index) {
    const token = this.authToken || '';
    const wsId = this.workspaceId || '';
    return `${this.baseURL}/api/support/${ticketId}/reply/${replyId}/attachment/${index}?token=${encodeURIComponent(token)}&workspace=${encodeURIComponent(wsId)}`;
  }
}

export default new CloudAPIClient();
